import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "./AuthProvider";
import { cognitoApi, credentialToJson, passkeysPossible, toWebAuthnRequestOptions } from "./cognito-api";
import { Dialog, ErrorState, btnPrimary, btnSecondary, inputClass } from "../components/ui";

/**
 * Step-up (requirements 13.3). Not a timer: before a security change, the
 * user must have proved who they are in the last fifteen minutes. If the
 * session's auth_time is older, this asks once - by passkey when the user
 * has one, else password and authenticator code - through Cognito's choice
 * flow, and swaps the fresh tokens into the session. The API checks the
 * same claim, so this is the polite half, not the only gate.
 *
 * A federated user (ADR-0014) has no password or code here: their proof
 * is a fresh round trip through their organisation's identity provider,
 * which brings them back to this page with a new auth_time.
 */
export const STEP_UP_MAX_AGE_S = 15 * 60;

interface StepUpState {
  /** Resolves when the session is fresh enough; rejects if the user cancels. */
  ensureFresh: () => Promise<void>;
  isFresh: () => boolean;
}

const StepUpContext = createContext<StepUpState | undefined>(undefined);

export function StepUpProvider({ children }: { children: ReactNode }) {
  const { session, config, me, replaceTokens, signIn } = useAuth();
  const [open, setOpen] = useState(false);
  const pending = useRef<{ resolve: () => void; reject: (e: unknown) => void }>();

  const isFresh = useCallback(() => Boolean(session?.authTime && Date.now() / 1000 - session.authTime < STEP_UP_MAX_AGE_S), [session]);

  const ensureFresh = useCallback(() => {
    if (isFresh()) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      pending.current = { resolve, reject };
      setOpen(true);
    });
  }, [isFresh]);

  const done = (ok: boolean, err?: unknown) => {
    setOpen(false);
    const p = pending.current;
    pending.current = undefined;
    if (ok) p?.resolve();
    else p?.reject(err ?? new Error("Confirmation cancelled"));
  };

  const value = useMemo(() => ({ ensureFresh, isFresh }), [ensureFresh, isFresh]);
  return (
    <StepUpContext.Provider value={value}>
      {children}
      {open && config && session && me?.identityProvider && <FederatedStepUp provider={me.identityProvider} onDone={done} signIn={signIn} />}
      {open && config && session && !me?.identityProvider && <StepUpDialog config={config} username={session.username ?? session.email ?? ""} accessToken={session.accessToken} onDone={done} replaceTokens={replaceTokens} />}
    </StepUpContext.Provider>
  );
}

export function useStepUp(): StepUpState {
  const ctx = useContext(StepUpContext);
  if (!ctx) throw new Error("useStepUp outside StepUpProvider");
  return ctx;
}

function FederatedStepUp({ provider, onDone, signIn }: { provider: string; onDone: (ok: boolean, err?: unknown) => void; signIn: ReturnType<typeof useAuth>["signIn"] }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog title="Confirm it's you" onClose={() => onDone(false)}>
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        This changes something security-sensitive, and your last sign-in was more than fifteen minutes ago. You sign in through <span className="font-medium">{provider}</span>, so we will send you there and bring you straight back here; then try again.
      </p>
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          className={btnPrimary}
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void signIn(window.location.pathname + window.location.search, { identityProvider: provider });
          }}
        >
          Continue to {provider}
        </button>
        <button type="button" className={btnSecondary} onClick={() => onDone(false)}>
          Cancel
        </button>
      </div>
    </Dialog>
  );
}

function StepUpDialog({
  config,
  username,
  accessToken,
  onDone,
  replaceTokens,
}: {
  config: NonNullable<ReturnType<typeof useAuth>["config"]>;
  username: string;
  accessToken: string | undefined;
  onDone: (ok: boolean, err?: unknown) => void;
  replaceTokens: ReturnType<typeof useAuth>["replaceTokens"];
}) {
  const api = useMemo(() => cognitoApi(config), [config]);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<{ name: string; session: string }>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  // Offer a passkey only when the user has one; Cognito answers a passkey-less WEB_AUTHN preference with a different challenge.
  const [hasPasskey, setHasPasskey] = useState(false);
  const possible = passkeysPossible();
  useEffect(() => {
    if (!possible || !accessToken) return;
    let live = true;
    api
      .listPasskeys(accessToken)
      .then((r) => live && setHasPasskey((r.Credentials ?? []).length > 0))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [api, accessToken, possible]);

  const finish = async (result: { AuthenticationResult?: Parameters<typeof replaceTokens>[0] }) => {
    if (!result.AuthenticationResult) throw new Error("Cognito answered without tokens");
    await replaceTokens(result.AuthenticationResult);
    onDone(true);
  };

  const withPasskey = async () => {
    // The previous answer stays on screen until this attempt has one of its own: an error that vanishes the moment
    // the user starts again reads as a glitch, not as "wrong password" (first walk, 2026-09-14).
    setBusy(true);
    try {
      const start = await api.initiateAuth(username, "WEB_AUTHN");
      if (start.ChallengeName !== "WEB_AUTHN" || !start.Session) throw new Error("Cognito did not offer a passkey challenge; use your password and code instead.");
      const options = toWebAuthnRequestOptions(JSON.parse(start.ChallengeParameters?.["CREDENTIAL_REQUEST_OPTIONS"] ?? "{}"));
      const assertion = await navigator.credentials.get(options);
      const result = await api.respondToChallenge("WEB_AUTHN", start.Session, { USERNAME: username, CREDENTIAL: JSON.stringify(credentialToJson(assertion)) });
      await finish(result);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const withPassword = async () => {
    // The previous answer stays on screen until this attempt has one of its own: an error that vanishes the moment
    // the user starts again reads as a glitch, not as "wrong password" (first walk, 2026-09-14).
    setBusy(true);
    try {
      if (!challenge) {
        const start = await api.initiateAuth(username, "PASSWORD", password);
        if (start.AuthenticationResult) return await finish(start);
        if (start.ChallengeName === "SOFTWARE_TOKEN_MFA" && start.Session) {
          setChallenge({ name: start.ChallengeName, session: start.Session });
          return;
        }
        throw new Error(`Cognito asked for ${start.ChallengeName ?? "something this page cannot answer"}; sign out and in again instead.`);
      }
      const result = await api.respondToChallenge(challenge.name, challenge.session, { USERNAME: username, SOFTWARE_TOKEN_MFA_CODE: code });
      await finish(result);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title="Confirm it's you" onClose={() => onDone(false)}>
      <p className="text-sm text-neutral-600 dark:text-neutral-400">This changes how you sign in, and your last sign-in was more than fifteen minutes ago. Prove it's you once and carry on.</p>
      {possible && hasPasskey && (
        <button type="button" className={`${btnPrimary} mt-4 w-full`} disabled={busy} onClick={() => void withPasskey()}>
          Use a passkey
        </button>
      )}
      <form
        className="mt-4 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void withPassword();
        }}
      >
        {!challenge ? (
          <label className="block text-sm">
            <span className="block font-medium">Password</span>
            <input type="password" autoComplete="current-password" className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
        ) : (
          <label className="block text-sm">
            <span className="block font-medium">Code from your authenticator app</span>
            <input inputMode="numeric" autoComplete="one-time-code" className={inputClass} value={code} onChange={(e) => setCode(e.target.value)} required pattern="[0-9]{6}" />
          </label>
        )}
        <div className="flex gap-2">
          <button type="submit" className={btnSecondary} disabled={busy}>
            {challenge ? "Confirm code" : "Continue with password"}
          </button>
          <button type="button" className={btnSecondary} onClick={() => onDone(false)}>
            Cancel
          </button>
        </div>
      </form>
      {error !== undefined && <ErrorState error={error} />}
    </Dialog>
  );
}
